/**
 * Data-hook voor de "per project"-weergave van de renovatietickets.
 *
 * Haalt de tickets gegroepeerd per renovatieproject en daarbinnen per
 * renovatieonderdeel op. Groeperen, sorteren, filteren én de tellers gebeuren
 * volledig backend-side (`GET /reno-helpdesk/tickets/grouped`); deze hook
 * platslaat de boom enkel naar rijen met groepeersleutels, want UniversalTable
 * verwacht een vlakke rijenlijst.
 *
 * Deze weergave pagineert niet — je moet volledige projecten kunnen open- en
 * dichtklikken — vandaar de vaste ticketlimiet. Wordt die bereikt, dan is
 * `truncated` true en moet de gebruiker verder filteren.
 *
 * @module RenoHelpdesk/Hooks/UseRenoTicketGroups
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  RenoGroupedTicketRow,
  RenoTicketGroupedResponse,
  RenoTicketProjectGroup,
  RenoTicketStatus,
  renoPartGroupKey,
  renoProjectGroupKey,
} from '../types/renoTicket';
import { buildRenoTicketParams, RenoTicketFilters } from '../utils/renoTicketParams';

/** Maximaal aantal tickets dat de gegroepeerde weergave in één keer ophaalt. */
export const RENO_GROUPED_TICKET_LIMIT = 1000;

interface UseRenoTicketGroupsOptions {
  filters: RenoTicketFilters;
  statuses: RenoTicketStatus[];
  /** Zet op false zolang de weergave niet actief is; dan wordt er niets opgehaald. */
  enabled?: boolean;
}

const EMPTY_RESPONSE: RenoTicketGroupedResponse = {
  groups: [],
  total: 0,
  openCount: 0,
  completionCount: 0,
  loaded: 0,
  ticketLimit: RENO_GROUPED_TICKET_LIMIT,
  truncated: false,
};

export function useRenoTicketGroups({
  filters,
  statuses,
  enabled = true,
}: UseRenoTicketGroupsOptions) {
  const { session } = useSession();
  const [data, setData] = useState<RenoTicketGroupedResponse>(EMPTY_RESPONSE);
  const [isLoading, setIsLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    if (!session || !enabled) return;
    setIsLoading(true);
    try {
      const params = buildRenoTicketParams(filters, {
        statuses,
        limit: RENO_GROUPED_TICKET_LIMIT,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      });

      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/grouped?${params}`,
        { method: 'GET' },
        session,
      );
      if (!res.ok) throw new Error('Ophalen van renovatietickets per project mislukt');

      const json = (await res.json()) as RenoTicketGroupedResponse;
      setData({ ...EMPTY_RESPONSE, ...json, groups: json.groups ?? [] });
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { module: 'reno-helpdesk', view: 'grouped-by-project' },
        extra: { filters, statuses },
      });
      toast.error(err.message || 'Fout bij ophalen van renovatietickets per project');
      setData(EMPTY_RESPONSE);
    } finally {
      setIsLoading(false);
    }
  }, [session, enabled, filters, statuses]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  /**
   * Vlakke rijen met groepeersleutels, in de volgorde die de backend bepaalde.
   * UniversalTable hergroepeert hierop (`groupBy` + `subGroupBy`).
   */
  const rows = useMemo<RenoGroupedTicketRow[]>(() => {
    let order = 0;
    return data.groups.flatMap((group) =>
      group.parts.flatMap((part) =>
        part.tickets.map((ticket) => ({
          ...ticket,
          projectGroupKey: renoProjectGroupKey(group.projectId),
          partGroupKey: renoPartGroupKey(group.projectId, part.partId),
          groupOrder: order++,
        })),
      ),
    );
  }, [data.groups]);

  /** Groepsmetadata per sleutel, voor de tellers in de accordeonkoppen. */
  const groupsByKey = useMemo(() => {
    const map = new Map<string, RenoTicketProjectGroup>();
    data.groups.forEach((group) => map.set(renoProjectGroupKey(group.projectId), group));
    return map;
  }, [data.groups]);

  const partsByKey = useMemo(() => {
    const map = new Map<string, { group: RenoTicketProjectGroup; part: RenoTicketProjectGroup['parts'][number] }>();
    data.groups.forEach((group) =>
      group.parts.forEach((part) =>
        map.set(renoPartGroupKey(group.projectId, part.partId), { group, part }),
      ),
    );
    return map;
  }, [data.groups]);

  return {
    groups: data.groups,
    groupsByKey,
    partsByKey,
    rows,
    total: data.total,
    openCount: data.openCount,
    completionCount: data.completionCount,
    loaded: data.loaded,
    ticketLimit: data.ticketLimit,
    truncated: data.truncated,
    isLoading,
    refetch: fetchGroups,
  };
}
